/**
 * Alchemy API Service (LEGACY)
 * Blockchain data provider for wallet tracking
 * Free tier: 300M compute units/month
 *
 * @deprecated Use `blockchainApi` from '@/services/blockchain' instead.
 * This service is kept for backward compatibility.
 *
 * The new blockchainApi provides:
 * - Multi-provider support (Blockbook, Alchemy, etc.)
 * - Automatic fallback between providers
 * - Better error handling and health monitoring
 *
 * Migration example:
 * ```typescript
 * // Old way (deprecated)
 * import { alchemyApi } from '@/services/alchemyApi';
 * const balance = await alchemyApi.getBalance(address);
 *
 * // New way (recommended)
 * import { blockchainApi } from '@/services/blockchain';
 * const balance = await blockchainApi.getBalance(address);
 * ```
 */

import axios from 'axios';
import type { AssetTransfer } from '../types/transaction.types';

const ALCHEMY_API_KEY = import.meta.env.VITE_ALCHEMY_API_KEY;
const ALCHEMY_NETWORK = import.meta.env.VITE_ALCHEMY_NETWORK || 'eth-mainnet';
const ALCHEMY_BASE_URL = `https://${ALCHEMY_NETWORK}.g.alchemy.com/v2/${ALCHEMY_API_KEY}`;

/**
 * Get ETH balance for an address
 */
export async function getBalance(address: string): Promise<string> {
  if (!ALCHEMY_API_KEY) {
    console.error('⚠️ Alchemy API key not configured. Please set VITE_ALCHEMY_API_KEY in .env');
    console.error('📖 See WALLET_TRACKER_SETUP.md for setup instructions');
    return '0x0';
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'eth_getBalance',
      params: [address, 'latest'],
      id: 1,
    });

    // Check for API error response
    if (response.data.error) {
      console.error('❌ Alchemy API error response:', response.data.error);
      throw new Error(response.data.error.message || 'Alchemy API error');
    }

    return response.data.result;
  } catch (error: any) {
    console.error('❌ Alchemy getBalance error for address:', address);
    console.error('Error details:', error.response?.data || error.message);
    console.error('API URL:', ALCHEMY_BASE_URL);
    console.error('API Key configured:', !!ALCHEMY_API_KEY);
    console.error('API Key preview:', ALCHEMY_API_KEY ? `${ALCHEMY_API_KEY.slice(0, 10)}...` : 'NOT SET');
    throw error;
  }
}

/**
 * Get ERC20 token balances for an address
 */
export async function getTokenBalances(address: string) {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return { address, tokenBalances: [] };
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'alchemy_getTokenBalances',
      params: [address],
      id: 1,
    });
    return response.data.result;
  } catch (error) {
    console.error('❌ Alchemy getTokenBalances error:', error);
    throw error;
  }
}

/**
 * Get token metadata (symbol, name, decimals)
 */
export async function getTokenMetadata(contractAddress: string) {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return null;
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'alchemy_getTokenMetadata',
      params: [contractAddress],
      id: 1,
    });
    return response.data.result;
  } catch (error) {
    console.error('❌ Alchemy getTokenMetadata error:', error);
    throw error;
  }
}

/**
 * Get asset transfers (transactions) for an address
 */
export interface GetAssetTransfersParams {
  fromAddress?: string;
  toAddress?: string;
  fromBlock?: string; // Hex format: '0x0'
  toBlock?: string; // Hex format: 'latest'
  category: ('external' | 'internal' | 'erc20' | 'erc721' | 'erc1155')[];
  maxCount?: string; // Hex format: '0x64' = 100
}

export async function getAssetTransfers(
  params: GetAssetTransfersParams
): Promise<AssetTransfer[]> {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return [];
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'alchemy_getAssetTransfers',
      params: [
        {
          ...params,
          withMetadata: true,
          maxCount: params.maxCount || '0x64', // 100 transactions
        },
      ],
      id: 1,
    });

    return response.data.result.transfers || [];
  } catch (error) {
    console.error('❌ Alchemy getAssetTransfers error:', error);
    throw error;
  }
}

/**
 * Resolve ENS name to address
 */
export async function resolveENSName(ensName: string): Promise<string | null> {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return null;
  }

  try {
    // Check if it's actually an ENS name (ends with .eth)
    if (!ensName.endsWith('.eth')) {
      return null;
    }

    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'eth_call',
      params: [
        {
          to: '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e', // ENS Registry
          data: `0x0178b8bf${ensName
            .split('.')
            .reverse()
            .map((label) => {
              const hex = Buffer.from(label).toString('hex').padStart(64, '0');
              return hex;
            })
            .join('')}`,
        },
        'latest',
      ],
      id: 1,
    });

    const result = response.data.result;
    if (result === '0x' || result === '0x0000000000000000000000000000000000000000000000000000000000000000') {
      return null;
    }

    // Extract address from result (last 40 characters)
    const address = '0x' + result.slice(-40);
    return address;
  } catch (error) {
    console.error('❌ Alchemy resolveENSName error:', error);
    return null;
  }
}

/**
 * Get transaction receipt
 */
export async function getTransactionReceipt(txHash: string) {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return null;
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'eth_getTransactionReceipt',
      params: [txHash],
      id: 1,
    });
    return response.data.result;
  } catch (error) {
    console.error('❌ Alchemy getTransactionReceipt error:', error);
    throw error;
  }
}

/**
 * Check if API key is configured
 */
export function isAlchemyConfigured(): boolean {
  return !!ALCHEMY_API_KEY;
}

/**
 * Get current block number
 */
export async function getBlockNumber(): Promise<number> {
  if (!ALCHEMY_API_KEY) {
    console.warn('⚠️ Alchemy API key not configured');
    return 0;
  }

  try {
    const response = await axios.post(ALCHEMY_BASE_URL, {
      jsonrpc: '2.0',
      method: 'eth_blockNumber',
      params: [],
      id: 1,
    });

    return parseInt(response.data.result, 16);
  } catch (error) {
    console.error('❌ Alchemy getBlockNumber error:', error);
    throw error;
  }
}

// Export all functions as a module
export const alchemyApi = {
  getBalance,
  getTokenBalances,
  getTokenMetadata,
  getAssetTransfers,
  resolveENSName,
  getTransactionReceipt,
  isAlchemyConfigured,
  getBlockNumber,
};
